import { Box, Typography } from '@mui/material';
import { useMemo, useState, type ReactElement } from 'react';

import { createAdminApi, type AdminApi, type AdminPrivacyRequest, type PrivacyRequestStatus } from '@/admin/adminApi';
import { EXPORT_LIMITATION, EXPORT_NOT_A_COMPLETE_ANSWER } from '@/admin/privacy/exportLimitation';
import { useAdminRead } from '@/admin/pilotage/useAdminRead';
import { accountDetailPath } from '@/admin/users/routes';
import { messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';
import { Button, Dialog, Field, SelectField } from '@/ui/kit';

import { InfoNote } from './InfoNote';
import {
  NOTE_LIMIT,
  STATUS_LABELS,
  TYPE_LABELS,
  buildPatch,
  editFormOf,
  formatDay,
  noteError,
  statusOptionsFor,
  typeNeedsExport,
  type EditForm,
} from './privacyRequestsModel';

/**
 * « Aucun responsable » porte une valeur **non vide** : MUI n'affiche pas le libelle
 * d'une option dont la valeur est `''` (il la prend pour « rien de choisi »).
 */
const NO_ASSIGNEE = 'none';

interface AssigneeOption {
  readonly userId: string;
  readonly email: string;
}

/**
 * Responsables proposes : les detenteurs d'un role (`GET /admin/admins`, ouvert
 * des `support`) ou, **a defaut**, l'acteur courant seul. Un acteur ne peut
 * toujours pas se retrouver sans personne a qui confier la demande.
 */
const readAssigneeOptions = async (api: AdminApi): Promise<readonly AssigneeOption[]> => {
  try {
    const holders = await api.listAdmins(1, 100);

    return holders.items.map((holder) => ({ userId: holder.userId, email: holder.email }));
  } catch {
    const me = await api.identity();

    return [{ userId: me.userId, email: me.email }];
  }
};

/**
 * Faire evoluer une demande : statut, responsable, note.
 *
 * Trois regles :
 * 1. **Seuls les passages que le serveur accepte sont proposes** (jamais en
 *    arriere) ; le serveur reste le juge.
 * 2. **Seul ce qui change est envoye** : renvoyer le tout reecrirait un
 *    responsable ou une note qu'un autre operateur vient de poser.
 * 3. **Clore est dit avant de l'etre** : la cloture est la trace de la reponse
 *    faite, elle n'est plus modifiable.
 *
 * Le dialogue **n'execute aucune action lourde** : l'export et l'anonymisation
 * restent sur la fiche du compte, derriere le dispositif sensible. Pour un droit
 * d'acces ou de portabilite, la limite de l'export est dite d'abord.
 */
export const EditRequestDialog = ({
  request,
  onClose,
  onSaved,
}: {
  readonly request: AdminPrivacyRequest;
  readonly onClose: () => void;
  readonly onSaved: (updated: AdminPrivacyRequest) => void;
}): ReactElement => {
  const http = useHttpClient();
  const { state: assignees } = useAdminRead(readAssigneeOptions, 0);
  const [form, setForm] = useState<EditForm>(() => editFormOf(request));
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const patch = useMemo(() => buildPatch(request, form), [request, form]);
  const unchanged = Object.keys(patch).length === 0;
  const noteProblem = noteError(form.note);
  const closing = form.status === 'closed';

  // Le responsable actuel reste proposable meme s'il a perdu son role : le retirer
  // de la liste afficherait « Aucun » alors qu'un responsable est bien enregistre.
  const options = useMemo(() => {
    const known = assignees.kind === 'ready' ? assignees.data : [];
    const list = known.map((option) => ({ value: option.userId, label: option.email }));

    if (request.assigneeUserId !== null && !list.some((option) => option.value === request.assigneeUserId)) {
      list.push({ value: request.assigneeUserId, label: request.assigneeEmail ?? 'Responsable actuel' });
    }

    return [{ value: NO_ASSIGNEE, label: 'Aucun responsable' }, ...list];
  }, [assignees, request]);

  const save = async (): Promise<void> => {
    if (unchanged || noteProblem !== undefined) {
      return;
    }

    setPending(true);
    setFailure(null);

    try {
      onSaved(await createAdminApi(http).updatePrivacyRequest(request.id, patch));
    } catch (error: unknown) {
      setFailure(messageOf(error));
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onClose={pending ? undefined : onClose}
      title="Faire évoluer la demande"
      description={`${TYPE_LABELS[request.type]} · reçue le ${formatDay(request.receivedAt)} · échéance le ${formatDay(request.dueAt)}`}
      actions={
        <>
          <Button onClick={onClose} disabled={pending}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={pending || unchanged || noteProblem !== undefined}
            onClick={() => {
              void save();
            }}
          >
            {pending ? 'Enregistrement…' : closing ? 'Clôturer la demande' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
        <Typography sx={{ fontSize: 14, overflowWrap: 'anywhere' }}>
          Demandeur : <strong>{request.requesterEmail}</strong>
        </Typography>
        <SelectField
          label="Statut"
          value={form.status}
          onChange={(value) => {
            setForm((previous) => ({ ...previous, status: value as PrivacyRequestStatus }));
          }}
          options={statusOptionsFor(request.status).map((value) => ({ value, label: STATUS_LABELS[value] }))}
        />
        <SelectField
          label="Responsable"
          value={form.assigneeUserId === '' ? NO_ASSIGNEE : form.assigneeUserId}
          onChange={(value) => {
            setForm((previous) => ({ ...previous, assigneeUserId: value === NO_ASSIGNEE ? '' : value }));
          }}
          options={options}
          hint={
            assignees.kind === 'loading'
              ? 'Chargement des responsables possibles…'
              : 'Les détenteurs d’un rôle d’administration. En cas d’échec de la liste : vous seul.'
          }
        />
        <Field
          label="Note de clôture"
          multiline
          minRows={3}
          value={form.note}
          onChange={(value) => {
            setForm((previous) => ({ ...previous, note: value }));
          }}
          error={noteProblem}
          hint={`Ce qui a été répondu à la personne, ${String(NOTE_LIMIT)} caractères au plus. Ne recopiez pas de donnée de santé.`}
        />
        {closing ? (
          <InfoNote title="La clôture est définitive">
            Une demande clôturée n’est plus modifiable : relisez la note avant d’enregistrer.
          </InfoNote>
        ) : null}
        {typeNeedsExport(request.type) ? (
          <InfoNote title="Avant tout export">
            {EXPORT_LIMITATION} {EXPORT_NOT_A_COMPLETE_ANSWER}
          </InfoNote>
        ) : null}
        <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
          Cet écran n’exécute aucune action lourde : l’export et l’anonymisation se déclenchent depuis la fiche du compte, avec motif, confirmation et mot de passe.
        </Typography>
        {request.userId === null ? (
          <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>Aucun compte actif ne porte l’adresse du demandeur.</Typography>
        ) : (
          <Box>
            <Button to={accountDetailPath(request.userId)}>Ouvrir la fiche du compte</Button>
          </Box>
        )}
        {failure === null ? null : (
          <Typography role="alert" sx={{ fontSize: 14, color: 'error.main' }}>
            {failure}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};
